/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import { useDispatch } from "react-redux";
import { useSelectedLocation } from "../../hooks";
import Dashboard from "./pages/Dashboard";
import AdminPage from "./pages/AdminPage";
import EmployeeProfile from "./pages/EmployeeProfile";
import ManageEmployees from "./pages/ManageEmployeeUpdates";
import NhoErrorModal from "./components/NhoErrorModal";
import { useTheme } from "../../theme";

// Props interface for the New Hire Onboarding component
interface NewHireOnboardingProps {
  initialRoute?: string;
  employeeId?: string;
  onBackPress?: () => void;
  onHomePress?: () => void; // Added for home navigation
  onAdminClick?: () => void;
  onBeginOnboardingClick?: (
    destination?: string,
    filters?: { employee: string; observationType: string }
  ) => void;
  onEmployeeProfileClick?: (employeeId: string) => void;
}

// Main entry point for the New Hire Onboarding module
const NewHireOnboarding: React.FC<NewHireOnboardingProps> = ({
  initialRoute,
  employeeId,
  onAdminClick,
  onBeginOnboardingClick,
  onEmployeeProfileClick,
  onHomePress,
}) => {
  const dispatch = useDispatch();
  const selectedLocation = useSelectedLocation();
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";

  // Initialize employee data for NHO when component mounts
  useEffect(() => {
    // In a real implementation, this would probably fetch from an API
    // For now, we're relying on data already available in the Redux store
  }, []);
  // State to manage current route and selected employee ID
  const [currentRoute, setCurrentRoute] = useState(
    initialRoute || "NHODashboard"
  );

  // Redirect to dashboard when location changes and user is on a non-dashboard page
  const prevLocationIdRef = useRef<string | undefined>(selectedLocation?.id);
  useEffect(() => {
    const prevId = prevLocationIdRef.current;
    prevLocationIdRef.current = selectedLocation?.id;
    if (
      prevId &&
      selectedLocation?.id &&
      prevId !== selectedLocation.id &&
      currentRoute !== "NHODashboard"
    ) {
      setCurrentRoute("NHODashboard");
    }
  }, [selectedLocation?.id, currentRoute]);
  const [currentEmployeeId, setCurrentEmployeeId] = useState(employeeId || "");

  // Add an effect to update the current route when initialRoute or employeeId props change
  useEffect(() => {
    // Always update the route when it changes in props
    if (initialRoute) {
      console.log(`Setting current route from prop: ${initialRoute}`);
      setCurrentRoute(initialRoute);
    }

    // Only update the employee ID when it's present in props
    if (employeeId) {
      console.log(`Setting employee ID from prop: ${employeeId}`);
      setCurrentEmployeeId(employeeId);
    }

    // Add special handling for NHOEmployeeProfile route
    if (initialRoute === "NHOEmployeeProfile" && !employeeId) {
      console.error(
        "Warning: NHOEmployeeProfile route specified but no employeeId provided!"
      );
    }
  }, [initialRoute, employeeId]);

  // Function to navigate between screens
  const navigate = (route: string, empId?: string) => {
    setCurrentRoute(route);
    if (empId) {
      setCurrentEmployeeId(empId);
    }
  };

  // Render the appropriate screen based on currentRoute
  const renderScreen = () => {
    switch (currentRoute) {
      case "NHOAdmin":
        return <AdminPage onBackPress={() => navigate("NHODashboard")} onHomePress={onHomePress} />;
      case "NHOEmployeeProfile":
        console.log(
          `Rendering NHOEmployeeProfile for ID index: ${currentEmployeeId}`
        );
        return (
          <EmployeeProfile
            employeeId={currentEmployeeId}
            onBackPress={() => navigate("NHODashboard")}
            onHomePress={onHomePress}
          />
        );
      case "NHOManageEmployees":
        return (
          <ManageEmployees
            onBackPress={() => navigate("NHODashboard")}
            onEmployeeProfileClick={(empId) => {
              if (onEmployeeProfileClick) {
                onEmployeeProfileClick(empId);
              } else {
                navigate("NHOEmployeeProfile", empId);
              }
            }}
          />
        );
      case "NHOBegin":
        // This would be the screen to show after clicking Begin and selecting options
        // For now, we'll reuse the Dashboard with a message
        return (
          <Dashboard
            onAdminClick={() => {
              // Navigate to admin page directly if no parent handler is provided
              if (onAdminClick) {
                onAdminClick();
              } else {
                navigate("NHOAdmin");
              }
            }}
            onBeginOnboardingClick={onBeginOnboardingClick}
            onManageEmployeesClick={() => navigate("NHOManageEmployees")}
            onEmployeeProfileClick={(selectedId) =>
              navigate("NHOEmployeeProfile", selectedId)
            }
            initialMessage="Begin onboarding process"
          />
        );
      case "NHOPulseCheckTouchPoint":
        // This would be the Pulse Check specific touchpoint interface
        return (
          <View style={styles.container}>
            <Dashboard
              onAdminClick={() => {
                // Navigate to admin page directly if no parent handler is provided
                if (onAdminClick) {
                  onAdminClick();
                } else {
                  navigate("NHOAdmin");
                }
              }}
              onBeginOnboardingClick={onBeginOnboardingClick}
              onManageEmployeesClick={() => navigate("NHOManageEmployees")}
              onEmployeeProfileClick={(selectedId) =>
                navigate("NHOEmployeeProfile", selectedId)
              }
              initialMessage="Pulse Check Touch Point"
            />
          </View>
        );
      case "NHOSpecificPeriodTouchPoint":
        // This would be the specific period (30/60/90/preferred) touchpoint interface
        return (
          <View style={styles.container}>
            <Dashboard
              onAdminClick={() => {
                // Navigate to admin page directly if no parent handler is provided
                if (onAdminClick) {
                  onAdminClick();
                } else {
                  navigate("NHOAdmin");
                }
              }}
              onBeginOnboardingClick={onBeginOnboardingClick}
              onManageEmployeesClick={() => navigate("NHOManageEmployees")}
              onEmployeeProfileClick={(selectedId) =>
                navigate("NHOEmployeeProfile", selectedId)
              }
              initialMessage="Specific Period Touch Point"
            />
          </View>
        );
      default:
        return (
          <Dashboard
            onAdminClick={() => {
              // Navigate to admin page directly if no parent handler is provided
              navigate("NHOAdmin");
            }}
            onBeginOnboardingClick={onBeginOnboardingClick}
            onManageEmployeesClick={() => navigate("NHOManageEmployees")}
            onEmployeeProfileClick={(selectedEmployeeId) => {
              // Update employeeId in parent component's state if needed
              if (onEmployeeProfileClick) {
                onEmployeeProfileClick(selectedEmployeeId);
              } else {
                // Navigate directly
                navigate("NHOEmployeeProfile", selectedEmployeeId);
              }
            }}
          />
        );
    }
  };
  console.log(
    `Current Route: ${currentRoute}, Employee ID: ${currentEmployeeId}`
  );
  return <View style={[styles.container, isDarkTheme && { backgroundColor: colors.background.page }]}>
    {renderScreen()}
    <NhoErrorModal />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F9FF",
  },
});

export default NewHireOnboarding;
